import api from "./api";

/**
 * ClubeConsulta PA integration through the authenticated Fynd API.
 * Provider credentials stay server-side; outages never switch providers.
 */

// ─── Tipos ─────────────────────────────────────────────────────────────────

export type PaTriageRole = "assistant" | "user";

export type PaTriageMessage = { role: PaTriageRole; content: string };

export type ManchesterAnswers = {
  flowchartId: string;
  confirmedIds: string[];
  unknownIds?: string[];
  complaint?: string;
};
export type ManchesterLevel = "red" | "orange" | "yellow" | "green" | "blue";
export type ManchesterFlowchart = {
  id: string;
  name: string;
  patientLabel?: string;
  searchTerms?: string[];
  discriminators: { id: string; level: ManchesterLevel; text: string; measure?: string }[];
};
export type ManchesterClassification = {
  level: ManchesterLevel;
  levelLabel: string;
  color: string;
  priority: number;
  targetMinutes: number | null;
  emergency: boolean;
  flowchartId: string;
  flowchartName: string;
  discriminatorId: string | null;
  discriminatorText: string | null;
  classifiedAt: string;
  requiresReview?: boolean;
  unknownDiscriminators?: { id: string; text: string }[];
};

export type TriagemResponse = {
  resposta: string;
  etapa: "triagem" | "confirmar_mais";
};

// ─── Triagem conversacional por IA ─────────────────────────────────────────

/** Análise descritiva gerada pela IA (sem nível de risco — quem classifica é o Manchester). */
export type TriageAiAnalysis = {
  summary?: string;
  chiefComplaint?: string;
  duration?: string;
  intensity?: string;
  redFlags?: string[];
  inferences?: { id: string; text?: string; evidence: string }[];
};

export type TriageAiSintomaResponse = {
  flowchart: ManchesterFlowchart | null;
  ai: { flowchartId: string; reason: string; alternatives: string[] } | null;
  iaDisponivel: boolean;
};

export type TriageAiChatResponse = {
  resposta: string;
  etapa: "triagem" | "classificar";
};

export type TriageAiClassificarResponse = {
  classification: ManchesterClassification;
  ai?: TriageAiAnalysis;
  answers: { flowchartId: string; confirmedIds: string[]; unknownIds?: string[]; complaint?: string };
};

/**
 * PA creation response. The legacy fallback field is always false.
 * Use the attendance ID for polling; deadline is compatibility metadata only.
 */
export type CreateAtendimentoResponse = {
  fallback: false;
  atendimentoId?: number;
  externalId?: string;
  status?: string;
  deadline?: string;
};

export type StatusAtendimento =
  | "waiting"
  | "picked"
  | "finished";

export type StatusAtendimentoResponse = {
  status: StatusAtendimento;
  fallback: false;
  patientLink?: string | null;
  deadline?: string | null;
  provider?: string;
};

export type AtendimentoAbertoResponse = {
  aberto: boolean;
  atendimentoId?: number;
  status?: StatusAtendimento;
  patientLink?: string | null;
  deadline?: string | null;
};

type ClubeconsultaCreateAtendimentoData = {
  especialidade?: string;
  triagem?: PaTriageMessage[];
  manchester?: ManchesterAnswers;
  ia?: TriageAiAnalysis;
  arquivos?: File[];
};

/** A IA pode levar mais tempo que o default do axios (respostas longas). */
const AI_TIMEOUT = { timeout: 65000 };

const BASE_CLUBECONSULTA = "/api/v2/app/telemedicina-clubeconsulta";

const mensagemPadrao =
  "Não foi possível prosseguir com a consulta. Tente novamente. Se o erro persistir, entre em contato com o suporte.";

// ─── ClubeConsulta (provedor primário / PA virtual) ─────────────────────────

async function clubeconsultaFluxogramas(): Promise<ManchesterFlowchart[]> {
  const response = await api.get(`${BASE_CLUBECONSULTA}/triagem/fluxogramas`);
  return response.data.flowcharts;
}

async function clubeconsultaClassificar(data: ManchesterAnswers): Promise<ManchesterClassification> {
  const response = await api.post(`${BASE_CLUBECONSULTA}/triagem/manchester`, data);
  return response.data.classification;
}

/** Sugere o fluxograma Manchester a partir da queixa livre (IA). */
async function clubeconsultaTriagemAiSintoma(complaint: string): Promise<TriageAiSintomaResponse> {
  try {
    return await api
      .post(`${BASE_CLUBECONSULTA}/triagem/ai/sintoma`, { complaint }, AI_TIMEOUT)
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/** Um turno da entrevista de triagem conduzida pela IA em linguagem simples. */
async function clubeconsultaTriagemAi(data: {
  flowchartId: string;
  messages: PaTriageMessage[];
  patientName?: string;
}): Promise<TriageAiChatResponse> {
  try {
    return await api
      .post(`${BASE_CLUBECONSULTA}/triagem/ai`, data, AI_TIMEOUT)
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/** Interpreta a conversa (IA) e classifica o risco com o motor Manchester. */
async function clubeconsultaTriagemAiClassificar(data: {
  flowchartId: string;
  messages: PaTriageMessage[];
  complaint?: string;
}): Promise<TriageAiClassificarResponse> {
  try {
    return await api
      .post(`${BASE_CLUBECONSULTA}/triagem/ai/manchester`, data, AI_TIMEOUT)
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/** Envia a conversa de triagem para a IA do ClubeConsulta (via fynd-api). */
async function clubeconsultaTriagem(
  messages: PaTriageMessage[],
  patientName?: string
): Promise<TriagemResponse> {
  try {
    return await api
      .post(`${BASE_CLUBECONSULTA}/triagem`, { messages, patientName })
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/**
 * Confirma o pré-atendimento e entra na fila de PA do ClubeConsulta.
 * Envia multipart: campos `especialidade` e `triagem` (JSON string) +
 * arquivos no campo `arquivos` (até 5; pdf/jpg/png/webp/doc/docx, 10MB cada).
 */
async function clubeconsultaCreateAtendimento(
  data: ClubeconsultaCreateAtendimentoData
): Promise<CreateAtendimentoResponse> {
  try {
    const form = new FormData();
    form.append("especialidade", data.especialidade || "Pronto Atendimento");
    form.append("triagem", JSON.stringify(data.triagem ?? []));
    if (data.manchester) form.append("manchester", JSON.stringify(data.manchester));
    if (data.ia) form.append("ia", JSON.stringify(data.ia));
    (data.arquivos ?? []).forEach((arquivo) => form.append("arquivos", arquivo));

    return await api
      .post(`${BASE_CLUBECONSULTA}/atendimento`, form, {
        headers: { Accept: "application/json" },
      })
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/** Polls queue status without automatic cancellation or provider switching. */
async function clubeconsultaStatus(
  atendimentoId: number
): Promise<StatusAtendimentoResponse> {
  try {
    return await api
      .get(`${BASE_CLUBECONSULTA}/atendimento/${atendimentoId}`)
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/** Verifica se há atendimento ClubeConsulta em aberto (aguardando/assumido). */
async function clubeconsultaAtendimentoAberto(): Promise<AtendimentoAbertoResponse> {
  try {
    return await api
      .get(`${BASE_CLUBECONSULTA}/atendimento-aberto`)
      .then((response) => response.data);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

/** Paciente desistiu enquanto aguardava na fila. */
async function clubeconsultaCancelar(atendimentoId: number): Promise<void> {
  try {
    await api.patch(`${BASE_CLUBECONSULTA}/atendimento/${atendimentoId}/cancelar`);
  } catch (error: any) {
    return Promise.reject({ ...error, message: error?.message || mensagemPadrao });
  }
}

const telemedicinaApi = {
  // ClubeConsulta (PA virtual — primário)
  clubeconsultaTriagem,
  clubeconsultaTriagemAiSintoma,
  clubeconsultaTriagemAi,
  clubeconsultaTriagemAiClassificar,
  clubeconsultaFluxogramas,
  clubeconsultaClassificar,
  clubeconsultaCreateAtendimento,
  clubeconsultaStatus,
  clubeconsultaAtendimentoAberto,
  clubeconsultaCancelar,
};

export default telemedicinaApi;
